/**
 * Seasonal coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_coloring`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const seasonalColoringPack: ToilePack = {
    slug: 'seasonal-coloring',
    category: 'coloring',
    name: 'Seasonal coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'autumn', label: 'Autumn', subject: 'autumn pumpkins, a friendly round ghost, a scarecrow and falling maple leaves'},
        {key: 'winter', label: 'Winter', subject: 'a snowman in a long scarf, a reindeer, a pine wreath and drifting snowflakes'},
        {key: 'spring', label: 'Spring', subject: 'tulips in bloom, a long-eared bunny, a watering can and a nesting bird'},
    ],
    styles: [
        {key: 'simple', label: 'Simple', style: 'drawn as a few large friendly shapes in one bold uniform black outline with wide open areas for a young child to color', palette: 'black ink on white paper only', framing: 'A flat 2D line drawing as printed in a coloring book, centered with a generous white margin'},
        {key: 'scene', label: 'Scene', style: 'arranged into one full scene with a layered background, drawn in a fine even black line with no shading, no gray tones and no fills', palette: 'black ink on white paper only', framing: 'A flat 2D line drawing as printed in a coloring book, the full page used inside a thin white margin'},
        {key: 'allover', label: 'All-over', style: 'scattered edge to edge as an all-over doodle field of small repeated motifs in an even thin black line', palette: 'black ink on white paper only', framing: 'A flat 2D line drawing on a plain white paper sheet, the motifs filling the page inside a white margin'},
    ],
    directions: ['in summer at the beach', 'for a toddler', 'as a birthday card'],
    seo: {
        title: 'Seasonal coloring pages: autumn, winter, spring',
        metaDescription: 'Nine seasonal coloring pages — autumn, winter, spring — simple, full scene or all-over pattern, one clean style for the whole year.',
        h1: 'Seasonal coloring pages: autumn, winter and spring in three styles',
        tagline: "Nine seasonal pages, three seasons, three styles each — one clean look for the whole year's fridge gallery.",
        intro: 'The page that changes with the calendar: pumpkins and a friendly ghost, a snowman and a reindeer, tulips and a bunny. A few large simple shapes for a young child, one full layered scene, or an all-over field of small repeated motifs. Ask for a different season or holiday and the set redraws in the same style.',
        useCases: [
            'a classroom seasonal bulletin board',
            'a fridge-gallery rotation',
            'a holiday party activity table',
            'a homeschool seasons unit',
        ],
        faq: [
            {q: 'What size do the pages print at?', a: 'Each page is drawn at portrait A4 proportions (832 by 1216) with a white margin, so it prints clean on standard paper.'},
            {q: 'Does the set cover every season?', a: 'These three sample autumn, winter and spring; ask for summer or a specific holiday and it is drawn in the same three styles.'},
            {q: 'Can I get a different season or holiday scene?', a: 'Yes — that is what the direction field on the page is for. Type a different season or holiday and it redraws.'},
        ],
    },
}
